'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import Link from 'next/link';
import { aiModelsApi, FlorenceCaptionResponse, FlorenceOCRResponse, FlorenceDetectResponse, FlorenceOpenVocabResponse, FlorenceGroundResponse, FlorenceDenseRegionResponse } from '@/lib/ai-models-api';

type FlorenceMode = 'caption' | 'ocr' | 'detect' | 'open-vocab' | 'ground' | 'dense-caption';

type ResultType = FlorenceCaptionResponse | FlorenceOCRResponse | FlorenceDetectResponse | FlorenceOpenVocabResponse | FlorenceGroundResponse | FlorenceDenseRegionResponse;

export default function FlorencePage() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [mode, setMode] = useState<FlorenceMode>('caption');
  const [detailLevel, setDetailLevel] = useState<'brief' | 'normal' | 'detailed'>('normal');
  const [withRegions, setWithRegions] = useState(false);
  const [classes, setClasses] = useState('');
  const [caption, setCaption] = useState('');
  const [model, setModel] = useState('florence-2-large');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResultType | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    // Get image dimensions
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
    img.src = URL.createObjectURL(file);

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      let response: ResultType;

      switch (mode) {
        case 'caption':
          response = await aiModelsApi.florenceCaption(image, { model, detail_level: detailLevel });
          break;
        case 'ocr':
          response = await aiModelsApi.florenceOCR(image, { model, with_regions: withRegions });
          break;
        case 'detect':
          response = await aiModelsApi.florenceDetect(image, { model });
          break;
        case 'open-vocab':
          if (!classes.trim()) {
            throw new Error('Please enter class names to detect');
          }
          response = await aiModelsApi.florenceDetectOpenVocab(image, classes.trim(), { model });
          break;
        case 'ground':
          if (!caption.trim()) {
            throw new Error('Please enter a caption to ground');
          }
          response = await aiModelsApi.florenceGround(image, caption.trim(), { model });
          break;
        case 'dense-caption':
          response = await aiModelsApi.florenceDenseCaption(image, { model });
          break;
        default:
          throw new Error('Unknown mode');
      }

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Draw bounding boxes on canvas
  useEffect(() => {
    if (!canvasRef.current || !imagePreview || !result || !imageSize) return;

    const hasBboxes = 'bboxes' in result && result.bboxes && result.bboxes.length > 0;
    if (!hasBboxes) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Set canvas size to match displayed image
      const displayWidth = canvas.parentElement?.clientWidth || img.width;
      const scale = displayWidth / img.width;
      const displayHeight = img.height * scale;

      canvas.width = displayWidth;
      canvas.height = displayHeight;

      // Draw image
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

      // Draw bboxes
      const bboxes = (result as FlorenceDetectResponse).bboxes;
      const labels = (result as FlorenceDetectResponse).labels || [];

      const colors = ['#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

      bboxes.forEach((bbox, idx) => {
        const [x1, y1, x2, y2] = bbox;
        const color = colors[idx % colors.length];

        // Scale coordinates
        const sx1 = x1 * scale;
        const sy1 = y1 * scale;
        const sx2 = x2 * scale;
        const sy2 = y2 * scale;

        // Draw box
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(sx1, sy1, sx2 - sx1, sy2 - sy1);

        // Draw label
        const label = labels[idx] || `Object ${idx + 1}`;
        ctx.font = '14px sans-serif';
        const textWidth = ctx.measureText(label).width;

        ctx.fillStyle = color;
        ctx.fillRect(sx1, sy1 - 20, textWidth + 8, 20);

        ctx.fillStyle = 'white';
        ctx.fillText(label, sx1 + 4, sy1 - 6);
      });
    };
    img.src = imagePreview;
  }, [result, imagePreview, imageSize]);

  const getModeDescription = (m: FlorenceMode) => {
    switch (m) {
      case 'caption': return 'Generate image captions with varying detail levels';
      case 'ocr': return 'Extract text from images (OCR)';
      case 'detect': return 'Detect objects in the image';
      case 'open-vocab': return 'Detect specific objects by name';
      case 'ground': return 'Find locations of objects mentioned in a caption';
      case 'dense-caption': return 'Generate captions for multiple regions';
      default: return '';
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">Florence-2 - Vision Foundation Model</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setImageSize(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium mb-2">Model</label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="florence-2-large">Florence-2 Large (Best Quality)</option>
                    <option value="florence-2-base">Florence-2 Base (Faster)</option>
                    <option value="florence-2-large-ft">Florence-2 Large Fine-tuned</option>
                    <option value="florence-2-base-ft">Florence-2 Base Fine-tuned</option>
                  </select>
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-sm font-medium mb-2">Task</label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as FlorenceMode)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="caption">Caption - Generate Description</option>
                    <option value="ocr">OCR - Extract Text</option>
                    <option value="detect">Detect - Object Detection</option>
                    <option value="open-vocab">Open Vocab - Detect Custom Classes</option>
                    <option value="ground">Ground - Locate Phrases in Caption</option>
                    <option value="dense-caption">Dense Caption - Multi-region Descriptions</option>
                  </select>
                  <p className="mt-1 text-xs text-[var(--text-muted)]">{getModeDescription(mode)}</p>
                </div>

                {/* Mode-specific options */}
                {mode === 'caption' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Detail Level</label>
                    <select
                      value={detailLevel}
                      onChange={(e) => setDetailLevel(e.target.value as 'brief' | 'normal' | 'detailed')}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    >
                      <option value="brief">Brief</option>
                      <option value="normal">Normal</option>
                      <option value="detailed">Detailed</option>
                    </select>
                  </div>
                )}

                {mode === 'ocr' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="withRegions"
                      checked={withRegions}
                      onChange={(e) => setWithRegions(e.target.checked)}
                      className="rounded border-[var(--border-subtle)]"
                    />
                    <label htmlFor="withRegions" className="text-sm">Include text region bounding boxes</label>
                  </div>
                )}

                {mode === 'open-vocab' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Classes to Detect</label>
                    <input
                      type="text"
                      value={classes}
                      onChange={(e) => setClasses(e.target.value)}
                      placeholder="person, car, dog, cat"
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-[var(--text-muted)]">Comma-separated class names</p>
                  </div>
                )}

                {mode === 'ground' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Caption to Ground</label>
                    <textarea
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="A cat sitting on a red couch next to a lamp"
                      rows={3}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-none"
                    />
                    <p className="mt-1 text-xs text-[var(--text-muted)]">Enter a caption to find object locations</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || loading || (mode === 'open-vocab' && !classes.trim()) || (mode === 'ground' && !caption.trim())}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Run Task'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Results</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-32 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image and run a task</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-32">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Processing with Florence-2...</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Image with bboxes */}
                    {'bboxes' in result && result.bboxes && result.bboxes.length > 0 && (
                      <div className="relative">
                        <canvas ref={canvasRef} className="w-full rounded-lg" />
                      </div>
                    )}

                    {/* Caption result */}
                    {'caption' in result && 'detail_level' in result && (
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Caption ({(result as FlorenceCaptionResponse).detail_level})</p>
                        <p className="text-lg">{(result as FlorenceCaptionResponse).caption}</p>
                      </div>
                    )}

                    {/* OCR result */}
                    {'text' in result && result.text && (
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Extracted Text</p>
                        <pre className="whitespace-pre-wrap font-mono text-sm">{result.text}</pre>
                      </div>
                    )}

                    {/* Detection labels */}
                    {'labels' in result && result.labels && result.labels.length > 0 && !('caption' in result && 'detail_level' in result) && (
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase mb-2">
                          Detections ({result.labels.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {result.labels.map((label, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-[var(--accent-soft)] text-[var(--accent)] rounded-full text-sm font-medium"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Grounding caption */}
                    {'caption' in result && !('detail_level' in result) && (
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Grounded Caption</p>
                        <p className="text-lg">{(result as FlorenceGroundResponse).caption}</p>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase">Inference Time</p>
                        <p className="text-lg font-semibold">{result.inference_time_ms.toFixed(0)}ms</p>
                      </div>
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase">Model</p>
                        <p className="text-lg font-semibold">{result.model}</p>
                      </div>
                    </div>

                    {/* Copy Button */}
                    <button
                      onClick={() => {
                        let text = '';
                        if ('caption' in result && 'detail_level' in result) {
                          text = (result as FlorenceCaptionResponse).caption;
                        } else if ('text' in result && result.text) {
                          text = result.text;
                        } else if ('labels' in result && result.labels) {
                          text = result.labels.join(', ');
                        }
                        if (text) navigator.clipboard.writeText(text);
                      }}
                      className="w-full py-3 bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-center font-medium rounded-lg transition-colors border border-[var(--border-subtle)]"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
